import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import { Box, Typography, FormControl, FormLabel } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const RadioGroupContainer = styled(FormControl)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const OptionsContainer = styled(Box, { shouldForwardProp: (prop) => prop !== 'row' })(({ theme, row }) => ({
  display: 'flex',
  flexDirection: row ? 'row' : 'column',
  gap: row ? theme.spacing(2) : theme.spacing(1.5),
  flexWrap: row ? 'wrap' : 'nowrap',
}));

const RadioOption = styled(Box, { shouldForwardProp: (prop) => prop !== 'selected' && prop !== 'isDisabled' })
(({ theme, selected, isDisabled }) => ({
  position: 'relative',
  opacity: isDisabled ? 0.5 : 1,
  cursor: isDisabled ? 'not-allowed' : 'pointer',
}));

const RadioButtonWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
}));

const RadioButtonControl = styled(Box, { shouldForwardProp: (prop) => prop !== 'selected' })(({ theme, selected }) => ({
  marginRight: theme.spacing(1),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledCheckCircleIcon = styled(CheckCircleIcon)({
  fontSize: '24px !important',
  width: '24px !important',
  height: '24px !important',
});

const OptionLabel = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  fontWeight: '500',
  color: '#999999',
  userSelect: 'none',
}));

const SuiRadioGroup = ({
  options,
  value,
  onChange,
  name,
  label,
  required,
  disabled,
  row,
  sx,
  labelKey,
  valueKey,
}) => {
  const [selectedValue, setSelectedValue] = useState(value);

  useEffect(() => {
    setSelectedValue(value);
  }, [value]);

  const handleChange = (option) => {
    if (disabled || option.disabled) return;
    
    const newValue = option[valueKey];
    setSelectedValue(newValue);
    
    if (onChange) {
      const syntheticEvent = {
        target: { name, value: newValue },
        preventDefault: () => {},
        stopPropagation: () => {},
      };
      onChange(syntheticEvent, newValue);
    }
  };

  return (
    <RadioGroupContainer disabled={disabled} sx={sx}>
            <FormLabel
              // htmlFor={id}
              required={required}
              sx={{
                color: "#000000", // Ensure the default color is black
                "&.Mui-focused": {
                  color: "#000000 !important", // Override MUI's default focus behavior
                },
              }}
            >
              {label}
            </FormLabel>
      <OptionsContainer row={row}>
        {options.map((option) => {
          const isSelected = option[valueKey] === selectedValue;
          const isOptionDisabled = disabled || option.disabled;
          const optionId = `${name}-${option[valueKey]}`;
          
          return (
            <RadioOption 
              key={option[valueKey]}
              selected={isSelected}
              isDisabled={isOptionDisabled}
            >
              <RadioButtonWrapper>
                <input
                  type="radio"
                  id={optionId}
                  name={name}
                  value={option[valueKey]}
                  checked={isSelected}
                  disabled={isOptionDisabled}
                  onChange={() => handleChange(option)}
                  style={{
                    position: 'absolute',
                    opacity: 0,
                    width: '100%',
                    height: '100%',
                    margin: 0,
                    cursor: isOptionDisabled ? 'not-allowed' : 'pointer',
                  }}
                />
                <RadioButtonControl selected={isSelected}>
                  {isSelected ? (
                    <StyledCheckCircleIcon sx={{ color: '#161740' }} />
                  ) : (
                    <Box sx={{ 
                      width: '24px', 
                      height: '24px', 
                      borderRadius: '50%', 
                      border: '2px solid',
                      borderColor: 'grey.500',
                    }} />
                  )}
                </RadioButtonControl>
                <OptionLabel component="label" htmlFor={optionId}>
                  {option[labelKey]}
                </OptionLabel>
              </RadioButtonWrapper>
            </RadioOption>
          );
        })}
      </OptionsContainer>      
    </RadioGroupContainer>
  );
};

SuiRadioGroup.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]).isRequired,
      label: PropTypes.node.isRequired,
      disabled: PropTypes.bool,
    })
  ).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  onChange: PropTypes.func,
  name: PropTypes.string.isRequired,
  label: PropTypes.node,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  row: PropTypes.bool,
  sx: PropTypes.object,
  labelKey: PropTypes.string,
  valueKey: PropTypes.string,
};

SuiRadioGroup.defaultProps = {
  value: '',
  onChange: null,
  label: null,
  required: false,
  disabled: false,
  row: false,
  sx: {},
  labelKey: 'label',
  valueKey: 'id',
};

export default SuiRadioGroup;